import React, { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { apiFetch } from '../../api/client';

interface TranslatedItem {
    id: string;
    type: 'category' | 'subcategory' | 'product' | 'variant';
    originalName: string;
    translatedName: string;
    originalDescription?: string;
    translatedDescription?: string;
    price?: number;
    categoryId?: string;
    categoryName?: string;
    subcategoryId?: string;
    subcategoryName?: string;
    productId?: string;
    productName?: string;
    selected: boolean;
    variantId?: string;
}

const AVAILABLE_LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' }
];

// Dizionario di traduzioni base
const TRANSLATION_DICTIONARY: Record<string, Record<string, string>> = {
    // Categorie comuni
    "APERITIVI": { "en": "Aperitifs", "de": "Aperitifs", "fr": "Apéritifs", "es": "Aperitivos" },
    "COCKTAIL": { "en": "Cocktails", "de": "Cocktails", "fr": "Cocktails", "es": "Cócteles" },
    "CAFFETTERIA": { "en": "Coffee Shop", "de": "Café", "fr": "Café", "es": "Cafetería" },
    "DOLCI": { "en": "Desserts", "de": "Nachspeisen", "fr": "Desserts", "es": "Postres" },
    "VINI": { "en": "Wines", "de": "Weine", "fr": "Vins", "es": "Vinos" },
    "BIRRE": { "en": "Beers", "de": "Biere", "fr": "Bières", "es": "Cervezas" },
    "BIBITE": { "en": "Drinks", "de": "Getränke", "fr": "Boissons", "es": "Bebidas" },

    // Prodotti comuni
    "CAFFÈ": { "en": "Coffee", "de": "Kaffee", "fr": "Café", "es": "Café" },
    "CAPPUCCINO": { "en": "Cappuccino", "de": "Cappuccino", "fr": "Cappuccino", "es": "Cappuccino" },
    "SPRITZ": { "en": "Spritz", "de": "Spritz", "fr": "Spritz", "es": "Spritz" },
    "AMERICANO": { "en": "Americano", "de": "Americano", "fr": "Americano", "es": "Americano" },
    "NEGRONI": { "en": "Negroni", "de": "Negroni", "fr": "Negroni", "es": "Negroni" },
    "BIRRA": { "en": "Beer", "de": "Bier", "fr": "Bière", "es": "Cerveza" },
    "VINO": { "en": "Wine", "de": "Wein", "fr": "Vin", "es": "Vino" },
    "ACQUA": { "en": "Water", "de": "Wasser", "fr": "Eau", "es": "Agua" }
};

const MenuTranslation: React.FC = () => {
    const { menuData, categories, subcategories, loading } = useTenant();
    const resolveTenantId = () => localStorage.getItem('selectedTenantId') || localStorage.getItem('tenantId');
    const [targetLanguage, setTargetLanguage] = useState('en');
    const [existingTranslations, setExistingTranslations] = useState<any[]>([]);
    const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);
    const [editingTranslation, setEditingTranslation] = useState<{
        language: string;
        category: string;
        itemId: string;
        field: 'name' | 'description';
        value: string;
    } | null>(null);
    const [viewMode, setViewMode] = useState<'selection' | 'translation' | 'preview' | 'manage'>('selection');
    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
    const [selectedSubcategories, setSelectedSubcategories] = useState<Set<string>>(new Set());
    const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
    const [translatedItems, setTranslatedItems] = useState<TranslatedItem[]>([]);
    const [isTranslating, setIsTranslating] = useState(false);
    const [useAutoTranslate, setUseAutoTranslate] = useState(true);

    // Debug: monitora i cambiamenti di existingTranslations
    useEffect(() => {
        console.log('🔄 existingTranslations changed:', existingTranslations.length, existingTranslations);
        console.log('📋 tenantId (selected or primary):', resolveTenantId());
    }, [existingTranslations]);

    // Funzione per traduzione automatica con MyMemory API (gratuita)
    const translateWithMyMemory = async (text: string, targetLang: string): Promise<string> => {
        try {
            const encodedText = encodeURIComponent(text);
            const url = `https://api.mymemory.translated.net/get?q=${encodedText}&langpair=it|${targetLang}`;

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error('MyMemory API fallita');
            }

            const data = await response.json();

            if (data.responseStatus === 200 && data.responseData) {
                return data.responseData.translatedText;
            } else {
                throw new Error('Risposta API non valida');
            }
        } catch (error) {
            console.error('Errore traduzione MyMemory:', error);
            // Fallback: prova con Microsoft Translator
            return translateWithMicrosoft(text, targetLang);
        }
    };

    // Funzione per traduzione con Microsoft Translator (fallback)
    const translateWithMicrosoft = async (text: string, targetLang: string): Promise<string> => {
        try {
            // Microsoft Translator Text API gratuita (senza autenticazione per limiti bassi)
            const url = `https://api.cognitive.microsofttranslator.com/translate?api-version=3.0&from=it&to=${targetLang}`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify([{ text }])
            });

            if (!response.ok) {
                throw new Error('Microsoft Translator fallita');
            }

            const data = await response.json();

            if (data && data[0] && data[0].translations && data[0].translations[0]) {
                return data[0].translations[0].text;
            } else {
                throw new Error('Risposta Microsoft non valida');
            }
        } catch (error) {
            console.error('Errore traduzione Microsoft:', error);
            // Ultimo fallback: usa il dizionario interno o mock
            return mockTranslate(text, targetLang);
        }
    };

    // Funzione di traduzione mock per i test (da rimuovere quando il backend sarà pronto)
    const mockTranslate = (text: string, targetLang: string): string => {
        const mockTranslations: { [key: string]: { [lang: string]: string } } = {
            'Pizza': { 'en': 'Pizza', 'fr': 'Pizza', 'de': 'Pizza', 'es': 'Pizza' },
            'Pasta': { 'en': 'Pasta', 'fr': 'Pâtes', 'de': 'Nudeln', 'es': 'Pasta' },
            'Antipasti': { 'en': 'Appetizers', 'fr': 'Entrées', 'de': 'Vorspeisen', 'es': 'Entrantes' },
            'Dolci': { 'en': 'Desserts', 'fr': 'Desserts', 'de': 'Nachspeisen', 'es': 'Postres' },
            'Bevande': { 'en': 'Drinks', 'fr': 'Boissons', 'de': 'Getränke', 'es': 'Bebidas' }
        };

        const upperText = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
        if (mockTranslations[upperText] && mockTranslations[upperText][targetLang]) {
            return mockTranslations[upperText][targetLang];
        }
        return text + ` (${targetLang})`; // Fallback semplice
    };

    const translateText = (text: string, targetLang: string): string => {
        // Cerca nel dizionario
        const upperText = text.toUpperCase();
        if (TRANSLATION_DICTIONARY[upperText] && TRANSLATION_DICTIONARY[upperText][targetLang]) {
            return TRANSLATION_DICTIONARY[upperText][targetLang];
        }

        // Cerca parole chiave parziali
        for (const [key, translations] of Object.entries(TRANSLATION_DICTIONARY)) {
            if (upperText.includes(key) && translations[targetLang]) {
                return text.replace(new RegExp(key, 'gi'), translations[targetLang]);
            }
        }

        // Fallback: aggiungi prefisso lingua
        return `[${targetLang.toUpperCase()}] ${text}`;
    };

    const generateTranslations = async () => {
        setIsTranslating(true);
        const items: TranslatedItem[] = [];

        try {
            // Aggiungi categorie selezionate
            for (const category of categories.filter(cat => selectedCategories.has(cat.id.toString()))) {
                const translatedName = useAutoTranslate
                    ? await translateWithMyMemory(category.name, targetLanguage)
                    : translateText(category.name, targetLanguage);
                items.push({
                    id: `cat-${category.id}`,
                    type: 'category',
                    originalName: category.name,
                    translatedName,
                    categoryId: category.id.toString(),
                    selected: true
                });
            }

            // Aggiungi sottocategorie selezionate
            for (const subcategory of subcategories.filter(sub => selectedSubcategories.has(sub.id.toString()))) {
                const parentCategory = categories.find(cat => String(cat.id) === String(subcategory.categoryId));
                const translatedName = useAutoTranslate
                    ? await translateWithMyMemory(subcategory.name, targetLanguage)
                    : translateText(subcategory.name, targetLanguage);
                const translatedDescription = subcategory.description
                    ? (useAutoTranslate
                        ? await translateWithMyMemory(subcategory.description, targetLanguage)
                        : translateText(subcategory.description, targetLanguage))
                    : undefined;

                items.push({
                    id: `sub-${subcategory.id}`,
                    type: 'subcategory',
                    originalName: subcategory.name,
                    translatedName,
                    originalDescription: subcategory.description,
                    translatedDescription,
                    categoryId: String(subcategory.categoryId),
                    categoryName: parentCategory?.name,
                    selected: true
                });
            }

            // Aggiungi prodotti selezionati
            if (menuData) {
                for (const product of menuData.filter(product => selectedProducts.has(product.id.toString()))) {
                    const category = categories.find(c => c.id === product.categoryId);
                    const subcategory = product.subcategoryId ? subcategories.find(s => s.id === product.subcategoryId) : undefined;
                    const translatedName = useAutoTranslate
                        ? await translateWithMyMemory(product.name, targetLanguage)
                        : translateText(product.name, targetLanguage);
                    const translatedDescription = product.description
                        ? (useAutoTranslate
                            ? await translateWithMyMemory(product.description, targetLanguage)
                            : translateText(product.description, targetLanguage))
                        : undefined;

                    items.push({
                        id: `prod-${product.id}`,
                        type: 'product',
                        originalName: product.name,
                        translatedName,
                        originalDescription: product.description,
                        translatedDescription,
                        price: product.price,
                        categoryId: product.categoryId?.toString(),
                        categoryName: category?.name,
                        subcategoryId: product.subcategoryId?.toString(),
                        subcategoryName: subcategory?.name,
                        selected: true
                    });

                    if (Array.isArray(product.variants) && product.variants.length > 0) {
                        for (const variant of product.variants) {
                            const translatedVariantName = useAutoTranslate
                                ? await translateWithMyMemory(variant.name, targetLanguage)
                                : translateText(variant.name, targetLanguage);

                            items.push({
                                id: `var-${product.id}-${variant.id}`,
                                type: 'variant',
                                originalName: variant.name,
                                translatedName: translatedVariantName,
                                price: variant.price,
                                categoryId: product.categoryId?.toString(),
                                subcategoryId: product.subcategoryId?.toString(),
                                productId: product.id.toString(),
                                productName: product.name,
                                selected: true,
                                variantId: String(variant.id)
                            });
                        }
                    }
                }
            }

            setTranslatedItems(items);
            setViewMode('translation');
        } catch (error) {
            console.error('Errore durante la traduzione:', error);
            alert('Errore durante la traduzione. Riprova più tardi.');
        } finally {
            setIsTranslating(false);
        }
    };

    const toggleCategorySelection = (categoryId: string) => {
        const newSelectedCategories = new Set(selectedCategories);
        const newSelectedSubcategories = new Set(selectedSubcategories);
        const newSelectedProducts = new Set(selectedProducts);

        if (newSelectedCategories.has(categoryId)) {
            newSelectedCategories.delete(categoryId);
            // Rimuovi sottocategorie e prodotti collegati
            subcategories
                .filter(sub => String(sub.categoryId) === categoryId)
                .forEach(sub => {
                    newSelectedSubcategories.delete(sub.id.toString());
                    if (menuData) {
                        menuData
                            .filter(product => product.subcategoryId && String(product.subcategoryId) === sub.id.toString())
                            .forEach(product => newSelectedProducts.delete(product.id.toString()));
                    }
                });
            if (menuData) {
                menuData
                    .filter(product => String(product.categoryId) === categoryId && (!product.subcategoryId || product.subcategoryId === ''))
                    .forEach(product => newSelectedProducts.delete(product.id.toString()));
            }
        } else {
            newSelectedCategories.add(categoryId);
            // Seleziona automaticamente sottocategorie e prodotti collegati
            subcategories
                .filter(sub => String(sub.categoryId) === categoryId)
                .forEach(sub => newSelectedSubcategories.add(sub.id.toString()));
            if (menuData) {
                menuData
                    .filter(product => String(product.categoryId) === categoryId)
                    .forEach(product => newSelectedProducts.add(product.id.toString()));
            }
        }

        setSelectedCategories(newSelectedCategories);
        setSelectedSubcategories(newSelectedSubcategories);
        setSelectedProducts(newSelectedProducts);
    };

    const toggleSubcategorySelection = (subcategoryId: string) => {
        const newSelectedSubcategories = new Set(selectedSubcategories);
        const newSelectedProducts = new Set(selectedProducts);

        if (newSelectedSubcategories.has(subcategoryId)) {
            newSelectedSubcategories.delete(subcategoryId);
            if (menuData) {
                menuData
                    .filter(product => product.subcategoryId && String(product.subcategoryId) === subcategoryId)
                    .forEach(product => newSelectedProducts.delete(product.id.toString()));
            }
        } else {
            newSelectedSubcategories.add(subcategoryId);
            if (menuData) {
                menuData
                    .filter(product => product.subcategoryId && String(product.subcategoryId) === subcategoryId)
                    .forEach(product => newSelectedProducts.add(product.id.toString()));
            }
        }

        setSelectedSubcategories(newSelectedSubcategories);
        setSelectedProducts(newSelectedProducts);
    };

    const toggleProductSelection = (productId: string) => {
        const newSelected = new Set(selectedProducts);
        if (newSelected.has(productId)) {
            newSelected.delete(productId);
        } else {
            newSelected.add(productId);
        }
        setSelectedProducts(newSelected);
    };

    const selectAllInCategory = (categoryId: string) => {
        if (!menuData) return;

        const newSelectedProducts = new Set(selectedProducts);
        const newSelectedSubcategories = new Set(selectedSubcategories);

        menuData
            .filter(product => String(product.categoryId) === categoryId)
            .forEach(product => newSelectedProducts.add(product.id.toString()));

        subcategories
            .filter(sub => String(sub.categoryId) === categoryId)
            .forEach(sub => newSelectedSubcategories.add(sub.id.toString()));

        setSelectedProducts(newSelectedProducts);
        setSelectedSubcategories(newSelectedSubcategories);
    };

    const selectAllInSubcategory = (subcategoryId: string) => {
        if (!menuData) return;

        const newSelectedProducts = new Set(selectedProducts);
        menuData
            .filter(product => product.subcategoryId && String(product.subcategoryId) === subcategoryId)
            .forEach(product => newSelectedProducts.add(product.id.toString()));

        const newSelectedSubcategories = new Set(selectedSubcategories);
        newSelectedSubcategories.add(subcategoryId);

        setSelectedProducts(newSelectedProducts);
        setSelectedSubcategories(newSelectedSubcategories);
    };

    const updateTranslation = (itemId: string, field: 'name' | 'description', value: string) => {
        setTranslatedItems(prev => prev.map(item => {
            if (item.id === itemId) {
                if (field === 'name') {
                    return { ...item, translatedName: value };
                } else {
                    return { ...item, translatedDescription: value };
                }
            }
            return item;
        }));
    };

    const saveTranslations = async () => {
        try {
            // Prima carica le traduzioni esistenti per questa lingua
            const tenantId = resolveTenantId();
            let existingCategories: any[] = [];
            let existingSubcategories: any[] = [];
            let existingMenuItems: any[] = [];
            let existingVariantsByProduct: Record<string, any[]> = {};

            try {
                const existingResponse = await apiFetch(`/api/menu/translations/${targetLanguage}`, {
                    headers: {
                        'X-Tenant-Id': tenantId || ''
                    }
                });

                if (existingResponse.ok) {
                    const existingData = await existingResponse.json();
                    existingCategories = existingData.categories || [];
                    existingSubcategories = existingData.subcategories || [];
                    existingMenuItems = existingData.menuItems || [];
                    if (Array.isArray(existingMenuItems)) {
                        existingVariantsByProduct = existingMenuItems.reduce<Record<string, any[]>>((acc, item) => {
                            if (Array.isArray(item.variants)) {
                                acc[item.id] = item.variants;
                            }
                            return acc;
                        }, {});
                    }
                    console.log('🔄 Loaded existing translations to merge:', existingData);
                }
            } catch (error) {
                console.log('ℹ️ No existing translations found, creating new ones');
            }

            // Prepara le nuove categorie tradotte
            const newCategories = translatedItems
                .filter(item => item.type === 'category' && item.selected)
                .map(item => ({
                    id: item.id.replace('cat-', ''),
                    originalName: item.originalName,
                    translatedName: item.translatedName,
                    originalDescription: item.originalDescription || '',
                    translatedDescription: item.translatedDescription || ''
                }));

            const newSubcategories = translatedItems
                .filter(item => item.type === 'subcategory' && item.selected)
                .map(item => ({
                    id: item.id.replace('sub-', ''),
                    originalName: item.originalName,
                    translatedName: item.translatedName,
                    originalDescription: item.originalDescription || '',
                    translatedDescription: item.translatedDescription || '',
                    categoryId: item.categoryId || ''
                }));

            // Prepara i nuovi prodotti tradotti
            const newMenuItems = translatedItems
                .filter(item => item.type === 'product' && item.selected)
                .map(item => ({
                    id: item.id.replace('prod-', ''),
                    originalName: item.originalName,
                    translatedName: item.translatedName,
                    originalDescription: item.originalDescription || '',
                    translatedDescription: item.translatedDescription || '',
                    price: item.price || 0,
                    categoryId: item.categoryId || '',
                    subcategoryId: item.subcategoryId || '',
                    variants: translatedItems
                        .filter(v => v.type === 'variant' && v.productId === item.id.replace('prod-', '') && v.selected)
                        .map(v => ({
                            id: v.variantId || v.id.replace(`var-${item.id.replace('prod-', '')}-`, ''),
                            originalName: v.originalName,
                            translatedName: v.translatedName,
                            price: v.price ?? 0
                        }))
                }));

            // Merge varianti esistenti non sovrascritte
            const newMenuItemsWithMergedVariants = newMenuItems.map(prod => {
                const existingVariants = existingVariantsByProduct[prod.id] || [];
                if (!existingVariants.length) {
                    return prod;
                }
                const incomingMap = new Map((prod.variants || []).map(v => [String(v.id), v]));
                const mergedVariants = existingVariants.reduce<any[]>((acc, existing) => {
                    const key = String(existing.id);
                    if (!incomingMap.has(key)) {
                        acc.push(existing);
                    }
                    return acc;
                }, []);
                const finalVariants = [...mergedVariants, ...(prod.variants || [])];
                return { ...prod, variants: finalVariants };
            });

            // Unisci le traduzioni esistenti con quelle nuove (le nuove sovrascrivono quelle esistenti)
            const mergedCategories = [...existingCategories];
            newCategories.forEach(newCat => {
                const existingIndex = mergedCategories.findIndex(existing => existing.id === newCat.id);
                if (existingIndex >= 0) {
                    mergedCategories[existingIndex] = newCat; // Sovrascrivi
                } else {
                    mergedCategories.push(newCat); // Aggiungi
                }
            });

            const mergedSubcategories = [...existingSubcategories];
            newSubcategories.forEach(newSub => {
                const existingIndex = mergedSubcategories.findIndex(existing => existing.id === newSub.id);
                if (existingIndex >= 0) {
                    mergedSubcategories[existingIndex] = newSub;
                } else {
                    mergedSubcategories.push(newSub);
                }
            });

            const mergedMenuItems = [...existingMenuItems];
            newMenuItemsWithMergedVariants.forEach(newItem => {
                const existingIndex = mergedMenuItems.findIndex(existing => existing.id === newItem.id);
                if (existingIndex >= 0) {
                    mergedMenuItems[existingIndex] = newItem; // Sovrascrivi
                } else {
                    mergedMenuItems.push(newItem); // Aggiungi
                }
            });

            const translationData = {
                language: targetLanguage,
                categories: mergedCategories,
                subcategories: mergedSubcategories,
                menuItems: mergedMenuItems.map(item => ({
                    ...item,
                    variants: item.variants || []
                }))
            };

            console.log('Invio payload:', translationData); // Debug
            const response = await apiFetch('/api/menu/translations', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Id': tenantId || ''
                },
                body: JSON.stringify(translationData)
            });

            if (response.ok) {
                const result = await response.json();
                alert(`Traduzioni salvate con successo in ${targetLanguage.toUpperCase()}!`);
                setViewMode('selection');
                setTranslatedItems([]);
                setSelectedCategories(new Set());
                setSelectedSubcategories(new Set());
                setSelectedProducts(new Set());
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Errore risposta:', errorData);
                throw new Error(errorData.message || 'Errore nel salvare le traduzioni');
            }
        } catch (err) {
            console.error('Errore nel salvare:', err);
            alert('Errore nel salvare le traduzioni');
        }
    };

    // Carica traduzioni esistenti
    const loadExistingTranslations = async () => {
        setIsLoadingTranslations(true);
        try {
            const tenantId = resolveTenantId();
            console.log('🔍 Loading translations for tenant:', tenantId); // Debug

            const response = await apiFetch('/api/menu/translations', {
                headers: {
                    'X-Tenant-Id': tenantId || ''
                }
            });

            if (response.ok) {
                const translations = await response.json();
                console.log('✅ Loaded translations:', translations); // Debug
                setExistingTranslations(translations);
            } else {
                console.error('❌ Response not ok:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('❌ Errore caricamento traduzioni:', error);
        } finally {
            setIsLoadingTranslations(false);
        }
    };

    // Carica traduzioni al mount e quando cambia modalità
    useEffect(() => {
        if (viewMode === 'manage') {
            loadExistingTranslations();
        }
    }, [viewMode]);

    // Aggiorna traduzione esistente
    const updateExistingTranslation = async (id: string, field: string, value: string) => {
        try {
            const response = await apiFetch(`/api/menu/translations/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ field, value })
            });

            if (response.ok) {
                // Ricarica le traduzioni
                loadExistingTranslations();
            }
        } catch (error) {
            console.error('Errore aggiornamento traduzione:', error);
        }
    };

    // Elimina traduzione
    const deleteTranslation = async (id: string) => {
        if (!confirm('Sei sicuro di voler eliminare questa traduzione?')) {
            return;
        }

        try {
            const tenantId = resolveTenantId();
            const response = await apiFetch(`/api/menu/translations/${id}`, {
                method: 'DELETE',
                headers: {
                    'X-Tenant-Id': tenantId || ''
                }
            });

            if (response.ok) {
                loadExistingTranslations();
                alert('Traduzione eliminata con successo');
            }
        } catch (error) {
            console.error('Errore eliminazione traduzione:', error);
            alert('Errore nell\'eliminare la traduzione');
        }
    };

    if (loading) {
        return <div style={{ padding: '20px' }}>Caricamento dati del menu...</div>;
    }

    if (viewMode === 'selection') {
        return (
            <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <h1>Traduzione Menu - Selezione Elementi</h1>
                    <button
                        onClick={() => setViewMode('manage')}
                        style={{
                            padding: '10px 20px',
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                        }}
                    >
                        📋 Gestisci Traduzioni Esistenti
                    </button>
                </div>

                <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '8px' }}>
                    <h3>Configurazione</h3>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Lingua di destinazione:
                        </label>
                        <select
                            value={targetLanguage}
                            onChange={(e) => setTargetLanguage(e.target.value)}
                            style={{ padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                            {AVAILABLE_LANGUAGES.map(lang => (
                                <option key={lang.code} value={lang.code}>{lang.name}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Metodo di traduzione:
                        </label>
                        <div style={{ display: 'flex', gap: '15px' }}>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="radio"
                                    name="translationMethod"
                                    checked={useAutoTranslate}
                                    onChange={() => setUseAutoTranslate(true)}
                                    style={{ marginRight: '5px' }}
                                />
                                🌐 Traduzione Automatica (AI)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="radio"
                                    name="translationMethod"
                                    checked={!useAutoTranslate}
                                    onChange={() => setUseAutoTranslate(false)}
                                    style={{ marginRight: '5px' }}
                                />
                                📖 Dizionario predefinito
                            </label>
                        </div>
                        <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                            {useAutoTranslate
                                ? 'Traduzione automatica GRATUITA via MyMemory API (10.000 parole/giorno)'
                                : 'Usa solo termini del dizionario predefinito (funziona offline)'
                            }
                        </small>
                    </div>

                    <div style={{ color: '#666', fontSize: '14px' }}>
                        <p><strong>Istruzioni:</strong></p>
                        <ul>
                            <li>Seleziona le categorie e/o prodotti individuali da tradurre</li>
                            <li>Usa "Seleziona tutti" per selezionare tutti i prodotti di una categoria</li>
                            <li>Le varianti (opzioni extra) collegate ai prodotti selezionati vengono incluse automaticamente</li>
                            <li>Clicca "Genera Traduzioni" per procedere</li>
                        </ul>

                        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '12px' }}>
                            <strong>🆓 Servizi di traduzione gratuiti utilizzati:</strong><br />
                            • MyMemory API (gratuita, 10.000 parole/giorno)<br />
                            • Microsoft Translator (fallback)<br />
                            • Dizionario locale predefinito
                        </div>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                    {categories.map(category => {
                        const productsInCategory = menuData?.filter(p => String(p.categoryId) === String(category.id)) || [];
                        const subcategoriesInCategory = subcategories.filter(sub => String(sub.categoryId) === String(category.id));
                        const standaloneProducts = productsInCategory.filter(product => !product.subcategoryId);
                        const productsBySubcategory = subcategoriesInCategory.map(sub => ({
                            subcategory: sub,
                            products: productsInCategory.filter(product => product.subcategoryId && String(product.subcategoryId) === String(sub.id))
                        }));
                        const totalProducts = productsInCategory.length;
                        const categorySelected = selectedCategories.has(category.id.toString());

                        return (
                            <div key={category.id} style={{
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                padding: '15px',
                                background: categorySelected ? '#e8f5e8' : 'white'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontSize: '16px', flex: 1 }}>
                                        <input
                                            type="checkbox"
                                            checked={categorySelected}
                                            onChange={() => toggleCategorySelection(category.id.toString())}
                                            style={{ marginRight: '10px', transform: 'scale(1.2)' }}
                                        />
                                        {category.name}
                                    </label>
                                    <span style={{ color: '#666', fontSize: '14px', marginRight: '10px' }}>
                                        {totalProducts} prodotti
                                    </span>
                                    <button
                                        onClick={() => selectAllInCategory(category.id.toString())}
                                        style={{
                                            padding: '5px 10px',
                                            fontSize: '12px',
                                            background: '#007bff',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Seleziona tutti
                                    </button>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {productsBySubcategory.map(({ subcategory, products }) => {
                                        const subSelected = selectedSubcategories.has(subcategory.id.toString());
                                        return (
                                            <div key={`sub-${subcategory.id}`} style={{
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '6px',
                                                padding: '10px',
                                                background: subSelected ? '#f0f4ff' : '#fafafa'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: '14px', flex: 1 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={subSelected}
                                                            onChange={() => toggleSubcategorySelection(subcategory.id.toString())}
                                                            style={{ marginRight: '6px' }}
                                                        />
                                                        {subcategory.name}
                                                    </label>
                                                    <span style={{ fontSize: '12px', color: '#666' }}>{products.length} prodotti</span>
                                                    <button
                                                        onClick={() => selectAllInSubcategory(subcategory.id.toString())}
                                                        style={{
                                                            padding: '4px 8px',
                                                            fontSize: '11px',
                                                            background: '#5c6bc0',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Tutti i prodotti
                                                    </button>
                                                </div>
                                                {subcategory.description && (
                                                    <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px' }}>
                                                        {subcategory.description}
                                                    </div>
                                                )}
                                                {products.length > 0 && (
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' }}>
                                                        {products.map(product => {
                                                            const productSelected = selectedProducts.has(product.id.toString());
                                                            return (
                                                                <label key={product.id} style={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    padding: '8px',
                                                                    background: productSelected ? '#e3f2fd' : '#f9f9f9',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    border: productSelected ? '1px solid #2196f3' : '1px solid #ddd'
                                                                }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={productSelected}
                                                                        onChange={() => toggleProductSelection(product.id.toString())}
                                                                        style={{ marginRight: '8px', marginTop: '2px' }}
                                                                    />
                                                                    <div style={{ flex: 1 }}>
                                                                        <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{product.name}</div>
                                                                        {product.description && (
                                                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                                                                {product.description}
                                                                            </div>
                                                                        )}
                                                                        <div style={{ fontSize: '12px', color: '#007bff', marginTop: '2px' }}>
                                                                            €{product.price?.toFixed(2) || '0.00'}
                                                                        </div>
                                                                        {Array.isArray(product.variants) && product.variants.length > 0 && (
                                                                            <div style={{ marginTop: '6px' }}>
                                                                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#ef6c00' }}>
                                                                                    Varianti incluse
                                                                                </div>
                                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                                                                    {product.variants.map(variant => {
                                                                                        const variantPrice = typeof variant.price === 'number' ? variant.price : Number(variant.price ?? 0);
                                                                                        const showPrice = Number.isFinite(variantPrice) && variantPrice !== 0;
                                                                                        return (
                                                                                            <span key={variant.id} style={{
                                                                                                background: '#fff8e1',
                                                                                                border: '1px solid #ffe0b2',
                                                                                                borderRadius: '12px',
                                                                                                padding: '2px 8px',
                                                                                                fontSize: '11px',
                                                                                                color: '#8d6e63'
                                                                                            }}>
                                                                                                {variant.name}
                                                                                                {showPrice ? ` (+€${variantPrice.toFixed(2)})` : ''}
                                                                                            </span>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {standaloneProducts.length > 0 && (
                                        <div style={{
                                            border: '1px dashed #ccc',
                                            borderRadius: '6px',
                                            padding: '10px',
                                            background: '#fdfdfd'
                                        }}>
                                            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px' }}>
                                                Prodotti senza sottocategoria
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' }}>
                                                {standaloneProducts.map(product => {
                                                    const productSelected = selectedProducts.has(product.id.toString());
                                                    return (
                                                        <label key={product.id} style={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            padding: '8px',
                                                            background: productSelected ? '#e3f2fd' : '#f9f9f9',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            border: productSelected ? '1px solid #2196f3' : '1px solid #ddd'
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={productSelected}
                                                                onChange={() => toggleProductSelection(product.id.toString())}
                                                                style={{ marginRight: '8px', marginTop: '2px' }}
                                                            />
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{product.name}</div>
                                                                {product.description && (
                                                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                                                        {product.description}
                                                                    </div>
                                                                )}
                                                                <div style={{ fontSize: '12px', color: '#007bff', marginTop: '2px' }}>
                                                                    €{product.price?.toFixed(2) || '0.00'}
                                                                </div>
                                                                {Array.isArray(product.variants) && product.variants.length > 0 && (
                                                                    <div style={{ marginTop: '6px' }}>
                                                                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#ef6c00' }}>
                                                                            Varianti incluse
                                                                        </div>
                                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                                                            {product.variants.map(variant => {
                                                                                const variantPrice = typeof variant.price === 'number' ? variant.price : Number(variant.price ?? 0);
                                                                                const showPrice = Number.isFinite(variantPrice) && variantPrice !== 0;
                                                                                return (
                                                                                    <span key={variant.id} style={{
                                                                                        background: '#fff8e1',
                                                                                        border: '1px solid #ffe0b2',
                                                                                        borderRadius: '12px',
                                                                                        padding: '2px 8px',
                                                                                        fontSize: '11px',
                                                                                        color: '#8d6e63'
                                                                                    }}>
                                                                                        {variant.name}
                                                                                        {showPrice ? ` (+€${variantPrice.toFixed(2)})` : ''}
                                                                                    </span>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '10px', color: '#666' }}>
                        Categorie selezionate: {selectedCategories.size} | Sottocategorie selezionate: {selectedSubcategories.size} | Prodotti selezionati: {selectedProducts.size}
                    </div>
                    <button
                        onClick={generateTranslations}
                        disabled={selectedCategories.size === 0 && selectedProducts.size === 0 || isTranslating}
                        style={{
                            padding: '12px 30px',
                            fontSize: '16px',
                            background: (selectedCategories.size > 0 || selectedProducts.size > 0) && !isTranslating ? '#28a745' : '#ccc',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: (selectedCategories.size > 0 || selectedProducts.size > 0) && !isTranslating ? 'pointer' : 'not-allowed'
                        }}
                    >
                        {isTranslating ? (
                            <>
                                🔄 Traduzione in corso...
                            </>
                        ) : (
                            `Genera Traduzioni (${selectedCategories.size + selectedProducts.size} elementi)`
                        )}
                    </button>
                </div>
            </div>
        );
    }

    if (viewMode === 'translation') {
        return (
            <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h1>Revisione Traduzioni - {AVAILABLE_LANGUAGES.find(l => l.code === targetLanguage)?.name}</h1>
                    <div>
                        <button
                            onClick={() => setViewMode('selection')}
                            style={{
                                padding: '8px 16px',
                                marginRight: '10px',
                                background: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            ← Torna alla Selezione
                        </button>
                        <button
                            onClick={() => setViewMode('preview')}
                            style={{
                                padding: '8px 16px',
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Anteprima →
                        </button>
                    </div>
                </div>

                <div style={{ marginBottom: '20px', padding: '10px', background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
                    <strong>Istruzioni:</strong> Controlla e modifica le traduzioni. Confronta originale e tradotto. Clicca "Anteprima" per vedere il risultato finale.
                </div>

                <div style={{ display: 'grid', gap: '20px' }}>
                    {translatedItems.map(item => (
                        <div key={item.id} style={{
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '20px',
                            background: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '15px',
                                paddingBottom: '10px',
                                borderBottom: '1px solid #eee'
                            }}>
                                {(() => {
                                    const typeConfig = item.type === 'category'
                                        ? { label: 'CATEGORIA', background: '#e8f5e8', color: '#2e7d32' }
                                        : item.type === 'subcategory'
                                            ? { label: 'SOTTOCATEGORIA', background: '#ede7f6', color: '#6a1b9a' }
                                            : item.type === 'variant'
                                                ? { label: 'VARIANTE', background: '#fff3e0', color: '#ef6c00' }
                                                : { label: 'PRODOTTO', background: '#e3f2fd', color: '#1565c0' };
                                    return (
                                        <span style={{
                                            padding: '4px 8px',
                                            background: typeConfig.background,
                                            color: typeConfig.color,
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            marginRight: '10px'
                                        }}>
                                            {typeConfig.label}
                                        </span>
                                    );
                                })()}
                                {item.categoryName && (
                                    <span style={{ color: '#666', fontSize: '14px' }}>
                                        {item.categoryName}
                                    </span>
                                )}
                                {item.subcategoryName && (
                                    <span style={{ color: '#8e24aa', fontSize: '13px', marginLeft: '10px' }}>
                                        {item.subcategoryName}
                                    </span>
                                )}
                                {item.type === 'variant' && item.productName && (
                                    <span style={{ color: '#1565c0', fontSize: '13px', marginLeft: 'auto' }}>
                                        {`Prodotto: ${item.productName}`}
                                    </span>
                                )}
                                {item.price && (
                                    <span style={{ color: '#007bff', fontSize: '14px', marginLeft: 'auto', fontWeight: 'bold' }}>
                                        €{item.price.toFixed(2)}
                                    </span>
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                                        Nome Originale:
                                    </label>
                                    <div style={{
                                        padding: '10px',
                                        background: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        fontSize: '16px'
                                    }}>
                                        {item.originalName}
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                                        Nome Tradotto:
                                    </label>
                                    <input
                                        type="text"
                                        value={item.translatedName}
                                        onChange={(e) => updateTranslation(item.id, 'name', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            border: '2px solid #007bff',
                                            borderRadius: '4px',
                                            fontSize: '16px',
                                            background: item.translatedName.startsWith('[') ? '#fff3cd' : 'white'
                                        }}
                                        placeholder={item.type === 'variant' ? 'Traduci nome variante...' : 'Inserisci traduzione...'}
                                    />
                                </div>
                            </div>

                            {item.originalDescription && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                                            Descrizione Originale:
                                        </label>
                                        <div style={{
                                            padding: '10px',
                                            background: '#f8f9fa',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '4px',
                                            minHeight: '60px',
                                            fontSize: '14px'
                                        }}>
                                            {item.originalDescription}
                                        </div>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                                            Descrizione Tradotta:
                                        </label>
                                        <textarea
                                            value={item.translatedDescription || ''}
                                            onChange={(e) => updateTranslation(item.id, 'description', e.target.value)}
                                            style={{
                                                width: '100%',
                                                minHeight: '60px',
                                                padding: '10px',
                                                border: '2px solid #007bff',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                background: (item.translatedDescription?.startsWith('[')) ? '#fff3cd' : 'white',
                                                resize: 'vertical'
                                            }}
                                            placeholder="Inserisci traduzione descrizione..."
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (viewMode === 'preview') {
        return (
            <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h1>Anteprima Finale - {AVAILABLE_LANGUAGES.find(l => l.code === targetLanguage)?.name}</h1>
                    <div>
                        <button
                            onClick={() => setViewMode('translation')}
                            style={{
                                padding: '8px 16px',
                                marginRight: '10px',
                                background: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            ← Modifica Traduzioni
                        </button>
                        <button
                            onClick={saveTranslations}
                            style={{
                                padding: '12px 24px',
                                background: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: 'bold'
                            }}
                        >
                            ✓ Salva Traduzioni
                        </button>
                    </div>
                </div>

                <div style={{ marginBottom: '20px', padding: '15px', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
                    <strong>Riepilogo:</strong> {translatedItems.length} elementi pronti per il salvataggio in lingua {AVAILABLE_LANGUAGES.find(l => l.code === targetLanguage)?.name}.
                </div>

                <div style={{ display: 'grid', gap: '15px' }}>
                    {translatedItems.filter(item => item.type === 'category').length > 0 && (
                        <div style={{ marginBottom: '30px' }}>
                            <h2 style={{ color: '#2e7d32', borderBottom: '2px solid #2e7d32', paddingBottom: '10px' }}>
                                Categorie ({translatedItems.filter(item => item.type === 'category').length})
                            </h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px', marginTop: '15px' }}>
                                {translatedItems
                                    .filter(item => item.type === 'category')
                                    .map(item => (
                                        <div key={item.id} style={{
                                            padding: '15px',
                                            background: '#e8f5e8',
                                            border: '2px solid #4caf50',
                                            borderRadius: '8px'
                                        }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#2e7d32' }}>
                                                {item.translatedName}
                                            </div>
                                            <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                                                Originale: {item.originalName}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {translatedItems.filter(item => item.type === 'subcategory').length > 0 && (
                        <div style={{ marginBottom: '30px' }}>
                            <h2 style={{ color: '#6a1b9a', borderBottom: '2px solid #6a1b9a', paddingBottom: '10px' }}>
                                Sottocategorie ({translatedItems.filter(item => item.type === 'subcategory').length})
                            </h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '15px', marginTop: '15px' }}>
                                {translatedItems
                                    .filter(item => item.type === 'subcategory')
                                    .map(item => (
                                        <div key={item.id} style={{
                                            padding: '15px',
                                            background: '#f3e5f5',
                                            border: '2px solid #ab47bc',
                                            borderRadius: '8px'
                                        }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#6a1b9a' }}>
                                                {item.translatedName}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#7b1fa2', marginTop: '4px' }}>
                                                Categoria: {item.categoryName}
                                            </div>
                                            <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                                                Originale: {item.originalName}
                                            </div>
                                            {item.translatedDescription && (
                                                <div style={{ marginTop: '8px', fontSize: '13px', color: '#4a148c' }}>
                                                    {item.translatedDescription}
                                                </div>
                                            )}
                                            {item.originalDescription && (
                                                <div style={{ marginTop: '4px', fontSize: '12px', color: '#8e24aa' }}>
                                                    Originale: {item.originalDescription}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {translatedItems.filter(item => item.type === 'product').length > 0 && (
                        <div>
                            <h2 style={{ color: '#1565c0', borderBottom: '2px solid #1565c0', paddingBottom: '10px' }}>
                                Prodotti ({translatedItems.filter(item => item.type === 'product').length})
                            </h2>
                            <div style={{ display: 'grid', gap: '15px', marginTop: '15px' }}>
                                {translatedItems
                                    .filter(item => item.type === 'product')
                                    .map(item => {
                                        const productId = item.id.replace('prod-', '');
                                        const variantItems = translatedItems.filter(variant => variant.type === 'variant' && variant.productId === productId);
                                        return (
                                            <div key={item.id} style={{
                                                padding: '20px',
                                                background: 'white',
                                                border: '2px solid #2196f3',
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#1565c0', marginBottom: '5px' }}>
                                                            {item.translatedName}
                                                        </div>
                                                        <div style={{ fontSize: '14px', color: '#666' }}>
                                                            Originale: {item.originalName}
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        fontWeight: 'bold',
                                                        fontSize: '18px',
                                                        color: '#28a745',
                                                        marginLeft: '15px'
                                                    }}>
                                                        €{item.price?.toFixed(2)}
                                                    </div>
                                                </div>

                                                {item.translatedDescription && (
                                                    <div style={{ marginTop: '10px' }}>
                                                        <div style={{ fontSize: '14px', color: '#333' }}>
                                                            {item.translatedDescription}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                                            Originale: {item.originalDescription}
                                                        </div>
                                                    </div>
                                                )}

                                                {item.categoryName && (
                                                    <div style={{
                                                        marginTop: '10px',
                                                        padding: '5px 10px',
                                                        background: '#f8f9fa',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        color: '#666'
                                                    }}>
                                                        Categoria: {item.categoryName}
                                                    </div>
                                                )}
                                                {item.subcategoryName && (
                                                    <div style={{
                                                        marginTop: '6px',
                                                        padding: '5px 10px',
                                                        background: '#ede7f6',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        color: '#6a1b9a'
                                                    }}>
                                                        Sottocategoria: {item.subcategoryName}
                                                    </div>
                                                )}

                                                {variantItems.length > 0 && (
                                                    <div style={{ marginTop: '15px' }}>
                                                        <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '6px', color: '#ef6c00' }}>
                                                            Varianti tradotte
                                                        </div>
                                                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            {variantItems.map(variant => {
                                                                const variantPrice = typeof variant.price === 'number' ? variant.price : null;
                                                                return (
                                                                    <li key={variant.id} style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        background: '#fff8e1',
                                                                        border: '1px solid #ffe0b2',
                                                                        borderRadius: '6px',
                                                                        padding: '6px 10px',
                                                                        fontSize: '13px'
                                                                    }}>
                                                                        <div>
                                                                            <div style={{ color: '#ef6c00', fontWeight: 600 }}>{variant.translatedName}</div>
                                                                            <div style={{ color: '#8d6e63' }}>Originale: {variant.originalName}</div>
                                                                        </div>
                                                                        {variantPrice !== null && (
                                                                            <span style={{ color: '#ef6c00', fontWeight: 600 }}>
                                                                                €{variantPrice.toFixed(2)}
                                                                            </span>
                                                                        )}
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (viewMode === 'manage') {
        return (
            <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <h1>Gestione Traduzioni</h1>
                    <button
                        onClick={() => setViewMode('selection')}
                        style={{
                            padding: '10px 20px',
                            background: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                        }}
                    >
                        ← Torna alla selezione
                    </button>
                </div>

                {isLoadingTranslations ? (
                    <div>Caricamento traduzioni...</div>
                ) : (
                    <div>
                        {existingTranslations.length === 0 ? (
                            <div style={{
                                padding: '40px',
                                textAlign: 'center',
                                background: '#f8f9fa',
                                borderRadius: '8px',
                                border: '1px solid #ddd'
                            }}>
                                <h3>Nessuna traduzione trovata</h3>
                                <p>Inizia creando le tue prime traduzioni nella sezione "Crea Traduzioni".</p>
                                <button
                                    onClick={() => setViewMode('selection')}
                                    style={{
                                        padding: '10px 20px',
                                        background: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        marginTop: '10px'
                                    }}
                                >
                                    Crea Nuove Traduzioni
                                </button>
                            </div>
                        ) : (
                            <div>
                                {AVAILABLE_LANGUAGES.map(language => {
                                    const translationsForLang = existingTranslations.filter(t => t.language === language.code);
                                    if (translationsForLang.length === 0) return null;

                                    return (
                                        <div key={language.code} style={{
                                            marginBottom: '30px',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                background: '#f8f9fa',
                                                padding: '15px',
                                                borderBottom: '1px solid #ddd'
                                            }}>
                                                <h3 style={{ margin: 0 }}>
                                                    {language.name} ({translationsForLang.length} traduzioni)
                                                </h3>
                                            </div>

                                            <div style={{ padding: '20px' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ background: '#f8f9fa' }}>
                                                            <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>Tipo</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>Originale</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>Traduzione</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>Azioni</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {translationsForLang.map((translation, index) => (
                                                            <tr key={index}>
                                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                                                    {translation.type === 'category'
                                                                        ? '📂 Categoria'
                                                                        : translation.type === 'subcategory'
                                                                            ? '🧩 Sottocategoria'
                                                                            : '🍽️ Prodotto'}
                                                                </td>
                                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                                                    <div>
                                                                        <strong>{translation.originalName}</strong>
                                                                        {translation.originalDescription && (
                                                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                                                {translation.originalDescription}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                                                    <div>
                                                                        {editingTranslation?.language === language.code &&
                                                                            editingTranslation?.itemId === translation.id?.toString() &&
                                                                            editingTranslation?.field === 'name' ? (
                                                                            <input
                                                                                type="text"
                                                                                value={editingTranslation.value}
                                                                                onChange={(e) => setEditingTranslation({
                                                                                    ...editingTranslation,
                                                                                    value: e.target.value
                                                                                })}
                                                                                onBlur={() => {
                                                                                    updateExistingTranslation(translation.id, 'translatedName', editingTranslation.value);
                                                                                    setEditingTranslation(null);
                                                                                }}
                                                                                onKeyPress={(e) => {
                                                                                    if (e.key === 'Enter') {
                                                                                        updateExistingTranslation(translation.id, 'translatedName', editingTranslation.value);
                                                                                        setEditingTranslation(null);
                                                                                    }
                                                                                }}
                                                                                style={{
                                                                                    border: '1px solid #ccc',
                                                                                    padding: '4px',
                                                                                    fontSize: '14px',
                                                                                    width: '100%'
                                                                                }}
                                                                                autoFocus
                                                                            />
                                                                        ) : (
                                                                            <strong
                                                                                onClick={() => setEditingTranslation({
                                                                                    language: language.code,
                                                                                    category: '',
                                                                                    itemId: translation.id?.toString() || '',
                                                                                    field: 'name',
                                                                                    value: translation.translatedName
                                                                                })}
                                                                                style={{ cursor: 'pointer', color: '#007bff' }}
                                                                                title="Clicca per modificare"
                                                                            >
                                                                                {translation.translatedName}
                                                                            </strong>
                                                                        )}

                                                                        {translation.translatedDescription && (
                                                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                                                {editingTranslation?.language === language.code &&
                                                                                    editingTranslation?.itemId === translation.id?.toString() &&
                                                                                    editingTranslation?.field === 'description' ? (
                                                                                    <textarea
                                                                                        value={editingTranslation.value}
                                                                                        onChange={(e) => setEditingTranslation({
                                                                                            ...editingTranslation,
                                                                                            value: e.target.value
                                                                                        })}
                                                                                        onBlur={() => {
                                                                                            updateExistingTranslation(translation.id, 'translatedDescription', editingTranslation.value);
                                                                                            setEditingTranslation(null);
                                                                                        }}
                                                                                        style={{
                                                                                            border: '1px solid #ccc',
                                                                                            padding: '4px',
                                                                                            fontSize: '12px',
                                                                                            width: '100%',
                                                                                            minHeight: '60px'
                                                                                        }}
                                                                                        autoFocus
                                                                                    />
                                                                                ) : (
                                                                                    <span
                                                                                        onClick={() => setEditingTranslation({
                                                                                            language: language.code,
                                                                                            category: '',
                                                                                            itemId: translation.id?.toString() || '',
                                                                                            field: 'description',
                                                                                            value: translation.translatedDescription
                                                                                        })}
                                                                                        style={{ cursor: 'pointer', color: '#007bff' }}
                                                                                        title="Clicca per modificare"
                                                                                    >
                                                                                        {translation.translatedDescription}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                                                    <button
                                                                        onClick={() => deleteTranslation(translation.id)}
                                                                        style={{
                                                                            padding: '5px 10px',
                                                                            background: '#dc3545',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '3px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '12px'
                                                                        }}
                                                                        title="Elimina traduzione"
                                                                    >
                                                                        🗑️
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    if (viewMode === 'manage') {
        return (
            <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <h1>Gestione Traduzioni</h1>
                    <button
                        onClick={() => setViewMode('selection')}
                        style={{
                            padding: '10px 20px',
                            background: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                        }}
                    >
                        ← Torna alla selezione
                    </button>
                </div>

                <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '8px' }}>
                    <h3>Seleziona lingua da visualizzare</h3>
                    <select
                        value={targetLanguage}
                        onChange={(e) => setTargetLanguage(e.target.value)}
                        style={{ padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '200px' }}
                    >
                        {AVAILABLE_LANGUAGES.map(lang => (
                            <option key={lang.code} value={lang.code}>{lang.name}</option>
                        ))}
                    </select>
                </div>

                {isLoadingTranslations ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        Caricamento traduzioni...
                    </div>
                ) : (
                    <div>
                        <h3>Menu Originale e Traduzioni ({AVAILABLE_LANGUAGES.find(l => l.code === targetLanguage)?.name})</h3>

                        {/* Categorie */}
                        <div style={{ marginBottom: '30px' }}>
                            <h4 style={{ color: '#007bff', marginBottom: '15px' }}>Categorie</h4>
                            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f8f9fa' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Originale (Italiano)</th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Traduzione ({AVAILABLE_LANGUAGES.find(l => l.code === targetLanguage)?.name})</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd', width: '100px' }}>Azioni</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories?.map(category => {
                                            const translation = existingTranslations.find(t =>
                                                t.language === targetLanguage &&
                                                t.categories?.some((c: any) => c.originalName === category.name)
                                            );
                                            const translatedCategory = translation?.categories?.find((c: any) => c.originalName === category.name);

                                            return (
                                                <tr key={category.id} style={{ borderBottom: '1px solid #eee' }}>
                                                    <td style={{ padding: '12px', fontWeight: 'bold' }}>{category.name}</td>
                                                    <td style={{ padding: '12px' }}>
                                                        {editingTranslation?.category === category.name && editingTranslation?.field === 'name' ? (
                                                            <input
                                                                type="text"
                                                                value={editingTranslation.value}
                                                                onChange={(e) => setEditingTranslation({ ...editingTranslation, value: e.target.value })}
                                                                onBlur={() => {
                                                                    // Salva la modifica
                                                                    setEditingTranslation(null);
                                                                }}
                                                                onKeyPress={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        setEditingTranslation(null);
                                                                    }
                                                                }}
                                                                style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
                                                                autoFocus
                                                            />
                                                        ) : (
                                                            <span
                                                                onClick={() => setEditingTranslation({
                                                                    language: targetLanguage,
                                                                    category: category.name,
                                                                    itemId: category.id.toString(),
                                                                    field: 'name',
                                                                    value: translatedCategory?.translatedName || ''
                                                                })}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    color: translatedCategory?.translatedName ? '#000' : '#999',
                                                                    fontStyle: translatedCategory?.translatedName ? 'normal' : 'italic'
                                                                }}
                                                            >
                                                                {translatedCategory?.translatedName || 'Nessuna traduzione disponibile'}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <button
                                                            onClick={() => setEditingTranslation({
                                                                language: targetLanguage,
                                                                category: category.name,
                                                                itemId: category.id.toString(),
                                                                field: 'name',
                                                                value: translatedCategory?.translatedName || ''
                                                            })}
                                                            style={{
                                                                padding: '4px 8px',
                                                                background: '#007bff',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '3px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px'
                                                            }}
                                                        >
                                                            ✏️
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Prodotti */}
                        <div>
                            <h4 style={{ color: '#28a745', marginBottom: '15px' }}>Prodotti del Menu</h4>
                            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f8f9fa' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Categoria</th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Nome Originale</th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Nome Tradotto</th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Descrizione Tradotta</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd', width: '100px' }}>Azioni</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {menuData?.map(product => {
                                            const category = categories?.find(c => c.id === product.categoryId);
                                            const translation = existingTranslations.find(t =>
                                                t.language === targetLanguage &&
                                                t.menuItems?.some((item: any) => item.originalName === product.name)
                                            );
                                            const translatedProduct = translation?.menuItems?.find((item: any) => item.originalName === product.name);

                                            return (
                                                <tr key={product.id} style={{ borderBottom: '1px solid #eee' }}>
                                                    <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>{category?.name}</td>
                                                    <td style={{ padding: '12px', fontWeight: 'bold' }}>
                                                        {product.name}
                                                        {product.description && (
                                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                                {product.description}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <span
                                                            style={{
                                                                cursor: 'pointer',
                                                                color: translatedProduct?.translatedName ? '#000' : '#999',
                                                                fontStyle: translatedProduct?.translatedName ? 'normal' : 'italic'
                                                            }}
                                                        >
                                                            {translatedProduct?.translatedName || 'Nessuna traduzione'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <span
                                                            style={{
                                                                cursor: 'pointer',
                                                                color: translatedProduct?.translatedDescription ? '#000' : '#999',
                                                                fontStyle: translatedProduct?.translatedDescription ? 'normal' : 'italic',
                                                                fontSize: '12px'
                                                            }}
                                                        >
                                                            {translatedProduct?.translatedDescription || 'Nessuna traduzione'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <button
                                                            style={{
                                                                padding: '4px 8px',
                                                                background: '#007bff',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '3px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px'
                                                            }}
                                                        >
                                                            ✏️
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return null;
};

export default MenuTranslation;
